import { PhoneInput } from "@/components/molecules/phone";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  isBlockAnswered,
  type NumberRequest,
  type UpdateNumberRequestData,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useState } from "react";
import type { Value as PhoneValue } from "react-phone-number-input";
import { EditableBlock } from "./editable-block";

/**
 * Everything that isn't a document, cut into blocks the client answers one at a
 * time (see `editable-block.tsx` for why).
 *
 * Both number-producing kinds now ask the same thing — we provision a new
 * number for either of them — so the only difference is that a client keeping
 * their number can tell us which one it is, so we know what will be pointed at
 * us later. Neither block is required: nothing here holds the file up.
 *
 * Each block saves only its own fields, so a half-answered page is still saved
 * work, and the request is the single source of truth: state is re-seeded from
 * it whenever it changes under us.
 */
type Save = (data: UpdateNumberRequestData) => Promise<boolean>;

export function RequestInformation({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: Save;
  /** The file is closed: what was answered is shown, nothing can be changed. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");

  // A SIP request has nothing to fill in here — its one field is the setup
  // description, which lives in the notes card.
  if (request.kind === "sip_trunk") return null;

  // Both blocks are optional, so a closed file can have nothing to show here —
  // and a title over an empty column reads as something that failed to load.
  const answer =
    request.kind === "port_existing"
      ? request.phone_number
      : request.desired_prefix;
  if (readOnly && !(answer || "").trim()) return null;

  return (
    <div className="space-y-3">
      <CardTitle>{t("numberRequests.details.title")}</CardTitle>

      {request.kind === "port_existing" && (
        <CurrentNumberBlock
          request={request}
          isSaving={isSaving}
          onSave={onSave}
          readOnly={readOnly}
        />
      )}

      {request.kind === "new_number" && (
        <PrefixBlock
          request={request}
          isSaving={isSaving}
          onSave={onSave}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

function SaveButton({ isSaving }: { isSaving: boolean }) {
  const { t } = useTranslation("numberRequests");

  return (
    <div className="flex justify-end">
      <Button type="submit" size="sm" disabled={isSaving}>
        {isSaving
          ? t("numberRequests.details.saving")
          : t("numberRequests.details.save")}
      </Button>
    </div>
  );
}

/**
 * `port_existing`: the number the client's customers already dial. It stays
 * with their operator — we only need to know it so we can recognise the calls
 * it forwards to us, and so support can tell the two numbers apart.
 */
function CurrentNumberBlock({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: Save;
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");
  const [phoneNumber, setPhoneNumber] = useState(request.phone_number);

  useEffect(() => setPhoneNumber(request.phone_number), [request.phone_number]);

  const submit = async (event: React.FormEvent, close: () => void) => {
    event.preventDefault();
    if (
      await onSave({
        phone_number: phoneNumber,
        answered_block: "current_number",
      })
    )
      close();
  };

  return (
    <EditableBlock
      title={t("numberRequests.details.currentNumber")}
      description={t("numberRequests.details.currentNumberHelp")}
      filled={request.phone_number.trim() !== ""}
      answered={isBlockAnswered(request, "current_number")}
      optional
      readOnly={readOnly}
      summary={
        request.phone_number ? (
          <p className="text-sm tabular-nums">{request.phone_number}</p>
        ) : undefined
      }
    >
      {(close) => (
        <form onSubmit={(event) => submit(event, close)} className="space-y-3">
          <Label htmlFor="phone-number" className="sr-only">
            {t("numberRequests.details.currentNumber")}
          </Label>
          <PhoneInput
            id="phone-number"
            value={phoneNumber as PhoneValue}
            onChange={(value) => setPhoneNumber((value as string) || "")}
            defaultCountry={(request.country as never) || (undefined as never)}
            placeholder={"+33 1 23 45 67 89"}
          />
          <SaveButton isSaving={isSaving} />
        </form>
      )}
    </EditableBlock>
  );
}

/** The digits the number we provision should start with. Optional. */
function PrefixBlock({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: Save;
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");
  const [prefix, setPrefix] = useState(request.desired_prefix);

  useEffect(() => setPrefix(request.desired_prefix), [request.desired_prefix]);

  const submit = async (event: React.FormEvent, close: () => void) => {
    event.preventDefault();
    if (
      await onSave({ desired_prefix: prefix, answered_block: "desired_prefix" })
    )
      close();
  };

  return (
    <EditableBlock
      title={t("numberRequests.details.desiredPrefix")}
      description={t("numberRequests.details.desiredPrefixHelp")}
      filled={Boolean(request.desired_prefix)}
      answered={isBlockAnswered(request, "desired_prefix")}
      optional
      readOnly={readOnly}
      summary={
        request.desired_prefix ? (
          <p className="text-sm tabular-nums">{request.desired_prefix}</p>
        ) : undefined
      }
    >
      {(close) => (
        <form onSubmit={(event) => submit(event, close)} className="space-y-3">
          <Label htmlFor="desired-prefix" className="sr-only">
            {t("numberRequests.details.desiredPrefix")}
          </Label>
          <Input
            id="desired-prefix"
            value={prefix}
            inputMode="numeric"
            maxLength={4}
            // Digits only, capped at 4 — anything longer is a whole number,
            // which is not what this field asks for.
            onChange={(event) =>
              setPrefix(event.target.value.replace(/\D/g, "").slice(0, 4))
            }
            placeholder={t("numberRequests.details.desiredPrefixPlaceholder")}
            className="max-w-[10rem] tabular-nums"
          />
          <SaveButton isSaving={isSaving} />
        </form>
      )}
    </EditableBlock>
  );
}
